<script setup lang="ts">
import { computed, ref } from "vue";
import { useAppCore } from "@lunagoai/vue-app-core";
import { RouterLink, RouterView, useRouter } from "vue-router";
import { ChevronLeft, ChevronRight, Menu, X } from "lucide-vue-next";
import { logout, type ApiPrincipal } from "../../api";
import { clearSession, isSuperAdministrator, sessionState } from "../../app/session";
import { messages, type Locale } from "../../i18n";
import GcsMark from "../branding/GcsMark.vue";
import AccountDock from "./AccountDock.vue";

const language = useAppCore().language!;
const router = useRouter();
const mobileOpen = ref(false);
const sidebarCollapsed = ref(false);
const principal = computed<ApiPrincipal | null>(() => sessionState().principal);
const locale = computed(() => language.currentLanguage.value as Locale);
const t = (key: keyof typeof messages["zh-TW"]) => messages[locale.value][key];
const isAdmin = computed(() => principal.value?.roles.includes("admin") || principal.value?.roles.includes("operator"));
const isSuperAdmin = computed(() => isSuperAdministrator());

const customerItems = computed(() => [
  { to: "/app/dashboard", label: t("dashboard"), icon: "⌂" },
  { to: "/app/reports", label: t("reports"), icon: "▤" },
  { to: "/app/reports/new", label: t("newReport"), icon: "+" },
  { to: "/app/tests/one-off", label: t("oneOffTestNav"), icon: "✦" },
  { to: "/app/account", label: t("account"), icon: "◎" },
]);
const adminItems = computed(() => [
  { to: "/admin/overview", label: t("adminOverview"), icon: "⌂" },
  { to: "/admin/flow", label: t("adminFlow"), icon: "➜" },
  { to: "/admin/sources", label: t("adminSources"), icon: "▥" },
  { to: "/admin/schedules", label: t("adminSchedules"), icon: "◫" },
  { to: "/admin/schedule-runs", label: t("adminScheduleRuns"), icon: "▤" },
  ...(isSuperAdmin.value ? [{ to: "/admin/models", label: t("adminModels"), icon: "◈" }] : []),
  { to: "/admin/usage", label: t("adminUsage"), icon: "#" },
  { to: "/admin/users", label: t("adminUsers"), icon: "◎" },
  ...(isSuperAdmin.value ? [{ to: "/admin/audit", label: t("adminAudit"), icon: "≡" }] : []),
]);

async function signOut(): Promise<void> {
  await logout().catch(() => undefined);
  clearSession();
  mobileOpen.value = false;
  await router.replace("/login");
}
</script>

<template>
  <div class="custom-scrollbar grid h-screen min-h-0 overflow-hidden bg-surface text-ui" :class="sidebarCollapsed ? 'lg:grid-cols-[4.5rem_minmax(0,1fr)]' : 'lg:grid-cols-[16rem_minmax(0,1fr)]'">
    <aside class="fixed inset-y-0 left-0 z-40 grid h-screen min-h-0 w-64 -translate-x-full grid-rows-[auto_minmax(0,1fr)_auto] border-r border-ui bg-surface-muted transition-[width,transform] lg:static lg:translate-x-0" :class="[mobileOpen ? 'translate-x-0' : '', sidebarCollapsed ? 'lg:w-[4.5rem]' : 'lg:w-64']">
      <div class="relative flex items-center border-b border-ui px-5 py-5" :class="sidebarCollapsed ? 'justify-center' : 'justify-between'">
        <div class="flex min-w-0 items-center gap-3" :class="sidebarCollapsed ? 'justify-center' : ''">
          <GcsMark :size="42" />
          <p v-if="!sidebarCollapsed" class="min-w-0 text-lg font-semibold leading-tight">{{ t("title") }}</p>
        </div>
        <button class="rounded-lg p-2 text-ui-muted hover:bg-surface-elevated lg:hidden" aria-label="Close menu" @click="mobileOpen = false"><X :size="18" /></button>
        <button v-if="!sidebarCollapsed" v-basehoverlabel="{ text: '收合側欄', placement: 'right' }" class="hidden rounded-lg p-2 text-ui-muted hover:bg-surface-elevated lg:block" aria-label="收合側欄" @click="sidebarCollapsed = true"><ChevronLeft :size="18" /></button>
        <button v-else v-basehoverlabel="{ text: '展開側欄', placement: 'right' }" class="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 rounded-lg p-1 text-ui-muted hover:bg-surface-elevated lg:block" aria-label="展開側欄" @click="sidebarCollapsed = false"><ChevronRight :size="16" /></button>
      </div>
      <nav class="custom-scrollbar min-h-0 overflow-y-auto py-5" :class="sidebarCollapsed ? 'px-2' : 'px-3'">
        <p v-if="!sidebarCollapsed" class="px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ui-dim">{{ t("workspace") }}</p>
        <div class="mt-2 space-y-1">
          <RouterLink v-for="item in customerItems" :key="item.to" v-basehoverlabel="{ text: sidebarCollapsed ? item.label : '', placement: 'right' }" :to="item.to" class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ui-muted transition hover:bg-surface-elevated hover:text-ui" :class="sidebarCollapsed ? 'justify-center' : ''" active-class="bg-primary/15 text-primary-light" @click="mobileOpen = false">
            <span class="grid size-8 shrink-0 place-items-center text-lg leading-none">{{ item.icon }}</span><span v-if="!sidebarCollapsed">{{ item.label }}</span>
          </RouterLink>
        </div>
        <template v-if="isAdmin">
          <p v-if="!sidebarCollapsed" class="mt-7 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ui-dim">{{ t("administration") }}</p>
          <div class="mt-2 space-y-1">
            <RouterLink v-for="item in adminItems" :key="item.to" v-basehoverlabel="{ text: sidebarCollapsed ? item.label : '', placement: 'right' }" :to="item.to" class="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ui-muted transition hover:bg-surface-elevated hover:text-ui" :class="sidebarCollapsed ? 'justify-center' : ''" active-class="bg-primary/15 text-primary-light" @click="mobileOpen = false">
              <span class="grid size-8 shrink-0 place-items-center text-lg leading-none">{{ item.icon }}</span><span v-if="!sidebarCollapsed">{{ item.label }}</span>
            </RouterLink>
          </div>
        </template>
      </nav>
      <div class="border-t border-ui"><AccountDock :collapsed="sidebarCollapsed" @logout="signOut" /></div>
    </aside>

    <main data-app-scroll-root class="custom-scrollbar relative min-h-0 min-w-0 overflow-x-hidden overflow-y-auto">
      <button class="fixed right-4 top-4 z-30 rounded-lg p-2 text-ui-muted hover:bg-surface-elevated lg:hidden" aria-label="Open menu" @click="mobileOpen = true"><Menu :size="20" /></button>
      <div class="min-h-full p-4 sm:p-6 lg:p-8"><div class="mx-auto w-full max-w-7xl"><RouterView /></div></div>
    </main>
    <button v-if="mobileOpen" class="fixed inset-0 z-30 bg-black/60 lg:hidden" aria-label="Close menu" @click="mobileOpen = false" />
  </div>
</template>
